/**
 * KAN-721: файлы материалов оператора: допустимые типы, лимит размера,
 * подпись размера, вид материала (картинка или документ).
 */
import type { InstructionAttachment } from '@/services/api/instructionAttachments';

export const MAX_MATERIAL_BYTES = 25 * 1024 * 1024;

/** Расширение -> MIME. Тип проверяем и по расширению: Windows отдаёт пустой type для doc/xls. */
const MATERIAL_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
};

const ALLOWED_MIME = new Set(Object.values(MATERIAL_TYPES));

/** Значение accept для input[type=file]: расширения и MIME. */
export const MATERIAL_ACCEPT = [
  ...Object.keys(MATERIAL_TYPES).map(ext => `.${ext}`),
  ...ALLOWED_MIME,
].join(',');

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Ошибка проверки файла или null, если файл подходит. */
export function validateMaterialFile(file: File | null): string | null {
  if (!file) return 'Выберите файл';
  const knownExtension = extensionOf(file.name) in MATERIAL_TYPES;
  if (!knownExtension && !ALLOWED_MIME.has(file.type)) {
    return 'Подходят JPG, PNG, PDF, Excel и Word';
  }
  if (file.size > MAX_MATERIAL_BYTES) return 'Файл больше 25 МБ';
  return null;
}

/** Размер файла по-русски: 512 Б, 12 КБ, 3,4 МБ. */
export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1).replace('.', ',')} МБ`;
}

/** Старый бэкенд не отдаёт kind: там были только картинки. */
export function isImageMaterial(item: InstructionAttachment): boolean {
  return (item.kind ?? 'image') === 'image';
}

/** Имя файла для подписи: исходное имя, иначе название. */
export function materialFileName(item: InstructionAttachment): string {
  return item.original_name || item.title;
}
